/**
 * Axios Configuration
 * Central configuration for axios with CSRF protection and credentials
 * This file MUST be imported before any other module that uses axios
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios to always include credentials (httpOnly cookies)
axios.defaults.withCredentials = true;

// localStorage keys for token backups (mobile/Safari fallback)
const TOKEN_STORAGE_KEY = 'auth_token_backup';
const CSRF_STORAGE_KEY = 'csrf_token_backup';

// CSRF Token Management
// The token is stored in a cookie by the server and read here for the header
// Falls back to localStorage for iOS Safari which may block cookie access
function getCsrfToken() {
  // First try to get from cookie
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        const token = decodeURIComponent(value);
        // Also save to localStorage as backup
        try {
          localStorage.setItem(CSRF_STORAGE_KEY, token);
        } catch {}
        return token;
      }
    }
  } catch (e) {
    console.warn('Cookie access failed:', e);
  }
  
  // Fallback to localStorage (for iOS Safari)
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Save CSRF token to localStorage (called when we get a fresh token)
function saveCsrfToken(token) {
  if (token) {
    try {
      localStorage.setItem(CSRF_STORAGE_KEY, token);
    } catch {}
  }
}

// Fetch a fresh CSRF token from the server
async function refreshCsrfToken() {
  try {
    const response = await axios.get(`${API}/api/auth/csrf-token`, {
      withCredentials: true,
      // Skip the interceptor for this request to avoid infinite loop
      headers: { 'X-Skip-CSRF-Refresh': 'true' }
    });
    const token = response.data.csrf_token;
    // Save to localStorage as backup for iOS Safari
    saveCsrfToken(token);
    return token;
  } catch (error) {
    console.warn('Failed to refresh CSRF token:', error);
    return null;
  }
}

// Helper to get backup token from localStorage
function getBackupToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Add CSRF token and Authorization header (from localStorage backup) to requests
axios.interceptors.request.use((config) => {
  // Only add CSRF token for methods that change state
  const methodsNeedingCsrf = ['post', 'put', 'patch', 'delete'];
  if (methodsNeedingCsrf.includes(config.method?.toLowerCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  
  // Add Authorization header from localStorage backup if no cookie auth
  // This helps mobile browsers that block third-party cookies
  if (!config.headers.Authorization) {
    const backupToken = getBackupToken();
    if (backupToken) {
      config.headers.Authorization = `Bearer ${backupToken}`;
    }
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle CSRF token refresh on 403 errors with retry
// Handle 401 errors by retrying with localStorage token, then dispatching session-expired
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized - try localStorage backup token first
    if (error.response?.status === 401 && 
        !originalRequest._authRetry &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/register') &&
        !originalRequest.url?.includes('/auth/csrf-token') &&
        !originalRequest.url?.includes('/auth/me')) {
      
      const backupToken = getBackupToken();
      
      // If we have a backup token and didn't already try it, retry with Authorization header
      if (backupToken && !originalRequest.headers?.Authorization) {
        originalRequest._authRetry = true;
        originalRequest.headers.Authorization = `Bearer ${backupToken}`;
        return axios(originalRequest);
      }
      
      // No backup token or retry also failed - dispatch session-expired
      window.dispatchEvent(new CustomEvent('session-expired', { 
        detail: { url: originalRequest.url } 
      }));
    }
    
    // If we get a CSRF error and haven't retried yet, try to refresh the token
    if (error.response?.status === 403 && 
        error.response?.data?.detail?.includes('CSRF') &&
        !originalRequest._csrfRetry &&
        !originalRequest.headers?.['X-Skip-CSRF-Refresh']) {
      
      originalRequest._csrfRetry = true;
      console.warn('CSRF token expired, refreshing...');
      
      // Try to refresh the CSRF token
      const newToken = await refreshCsrfToken();
      
      if (newToken) {
        // Retry the original request with the new token
        originalRequest.headers['X-CSRF-Token'] = newToken;
        return axios(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

// Initialize CSRF token - call this early in app lifecycle
// Especially important for iOS Safari which may not have cookie access
export async function initializeCsrf(forceRefresh = false) {
  // Check if we already have a token
  const existingToken = getCsrfToken();
  if (!existingToken || forceRefresh) {
    // No token found or forced refresh, fetch a fresh one
    if (!existingToken) {
      console.log('No CSRF token found, fetching fresh token...');
    }
    await refreshCsrfToken();
  }
}

export default axios;
